import { useState, useEffect, useMemo } from 'react';
import {
  Checkbox,
  Alert,
  Stack,
  Loader,
  Text,
  Group,
  TextInput,
  SegmentedControl,
} from '@mantine/core';
import { IconAlertCircle, IconPackage, IconSearch } from '@tabler/icons-react';
import { useRef } from 'react';
import { PackageService } from '../../types/services/PackageService';
import { CombinedPackageService } from '../../types/services/CombinedPackageService';
import PackageItem from './PackageItem';
import PackageActionBar from './PackageActionBar';
import DisposeModal from './DisposeModal';
import ReturnModal from './ReturnModal';
import { PackageFilterType, usePackageFilterContext } from '../../contexts/PackageFilterContext';

// Update Package interface to match the API response structure
export interface Package {
  id: number;
  description: string;
  weight: string;
  dimensions: {
    length: string;
    width: string;
    height: string;
  };
  customValue: string;
  quantity: number;
  shippingCost: string;
  status: string;
  isDraft: boolean;
  item: Array<{
    id: number;
    name: string;
    price: string;
    quantity: number;
  }>;
  shipment: {
    id: number;
    order_id: string;
    state: string;
  } | null;
  combinedPackage: any;
  // These fields might not be in API but needed for component
  name?: string;
  retailer?: string;
  receivedDate?: string;
  type?: 'regular' | 'combined' | 'shipped';
  createdAt?: string;
}

// API response types
// API response type for received packages
export interface ReceivedPackageItem {
  id: number;
  description: string;
  weight: string;
  length: string;
  width: string;
  height: string;
  invoice_image: string | null;
  image: string | null;
  note: string | null;
  custom_value: string;
  quantity: number;
  shipping_cost: string;
  isDraft: boolean;
  created_at: string;
  updated_at: string;
  user_id: string;
  combinedPackageId: number | null;
  item: Array<{
    id: number;
    name: string;
    description: string;
    weight: string;
    dimensions: string;
    category: string;
    price: string;
    currency: string;
    quantity: number;
    packageId: number;
  }>;
  shipment: any | null;
  combinedPackage: any | null;
}

// API response type for shipped packages
// API response type for combined packages

export interface ReceivedPackageResponse {
  data: ReceivedPackageItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface PackageListProps {
  onSelectPackages?: (packageIds: string[]) => void;
}

const PackageList = ({ onSelectPackages }: PackageListProps = {}) => {
  const [receivedPackages, setReceivedPackages] = useState<Package[]>([]);
  const [combinedPackages, setCombinedPackages] = useState<Package[]>([]);
  const [shippedPackages, setShippedPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { state: filterState, dispatch: filterDispatch } = usePackageFilterContext();
  const statusFilter = filterState.statusFilter;
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [disposeModalOpen, setDisposeModalOpen] = useState(false);
  const [returnModalOpen, setReturnModalOpen] = useState(false);

  // Track if data has been loaded for each filter type (for caching)
  const [dataLoaded, setDataLoaded] = useState({
    received: false,
    consolidated: false,
    shipped: false
  });

  // Track pagination state for each filter type
  const [paginationState, setPaginationState] = useState({
    received: { currentPage: 1, totalPages: 1 },
    consolidated: { currentPage: 1, totalPages: 1 },
    shipped: { currentPage: 1, totalPages: 1 }
  });
  const [pageLimit] = useState(10);

  // Helper function to determine if a package can be selected for actions
  const canSelectPackage = (pkg: Package) => {
    return pkg.type !== 'shipped' && pkg.status !== 'SHIPPED';
  };

  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  // Update window width when resized
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(typeof window !== 'undefined' ? window.innerWidth : 1200);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  const isMobile = windowWidth < 768;

  // Fetch received packages
  const fetchReceivedPackages = async (page = paginationState.received.currentPage) => {
    try {
      const response = await PackageService.getPackage(
        undefined, page, pageLimit, undefined, undefined, false, false, false, undefined
      );
      const packagesArray = response.data ? response.data : [];
      const pagination = response.pagination;
      let processed: Package[] = [];

      if (Array.isArray(packagesArray)) {
        processed = packagesArray.map((pkg: any) => ({
          ...pkg,
          status: pkg.shipment?.state === 'APPROVED' || pkg.shipment?.state === 'REQUESTED' ? 'SHIPPED' : 'RECEIVED',
          type: 'regular' as const,
          receivedDate: pkg.created_at || new Date().toISOString(),
        }));
        setReceivedPackages(processed);
      }

      if (pagination) {
        setPaginationState(prev => ({
          ...prev,
          received: {
            currentPage: pagination.page || 1,
            totalPages: pagination.pages || 1
          }
        }));
      }

      setDataLoaded(prev => ({ ...prev, received: true }));
      return processed;
    } catch (err) {
      setError('Failed to load received packages. Please try again.');
      throw err;
    }
  };

  // Fetch consolidated packages
  const fetchConsolidatedPackages = async () => {
    try {
      // Convert page to string since API expects string parameters
      const response = await CombinedPackageService.getPackageCombine(
        false, 'DONE', undefined, undefined, undefined, undefined
      );
      const consolidatedArray = response.data ? response.data : [];
      let processed: Package[] = [];

      if (Array.isArray(consolidatedArray)) {
        processed = consolidatedArray.map((pkg: any) => ({
          ...pkg,
          type: 'combined',
          status: 'CONSOLIDATED',
          receivedDate: pkg.createdAt || new Date().toISOString(),
        }));
        setCombinedPackages(processed);
      }

      // Store pagination info if available
      const pagination = response.pagination;
      if (pagination) {
        setPaginationState(prev => ({
          ...prev,
          consolidated: {
            currentPage: pagination.page || 1,
            totalPages: pagination.pages || 1
          }
        }));
      }

      setDataLoaded(prev => ({ ...prev, consolidated: true }));
      return processed;
    } catch (err) {
      setError('Failed to load consolidated packages. Please try again.');
      throw err;
    }
  };

  // Fetch shipped packages
  const fetchShippedPackages = async (page = paginationState.shipped.currentPage) => {
    try {
      const response = await PackageService.getPackage(
        undefined, page, pageLimit, undefined, undefined, false, true, undefined, undefined
      );
      const shippedArray = response.data ? response.data : response;
      let processed: Package[] = [];

      if (Array.isArray(shippedArray)) {
        processed = shippedArray.map((pkg: any) => ({
          ...pkg,
          type: 'shipped',
          status: 'SHIPPED',
          receivedDate: pkg.created_at || new Date().toISOString(),
        }));
        setShippedPackages(processed);
      }

      // Store pagination info if available
      const pagination = response.pagination;
      if (pagination) {
        setPaginationState(prev => ({
          ...prev,
          shipped: {
            currentPage: pagination.page || 1,
            totalPages: pagination.pages || 1
          }
        }));
      }

      setDataLoaded(prev => ({ ...prev, shipped: true }));
      return processed;
    } catch (err) {
      setError('Failed to load shipped packages. Please try again.');
      throw err;
    }
  };

  // Keep track of fetch requests to prevent duplicate calls
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);

  // Master function to fetch packages based on filter
  const fetchPackages = async (filter: string, page?: number) => {
    // Prevent duplicate calls while already loading
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      if (filter === 'all') {
        // Fetch all package types in parallel if they haven't been loaded yet or if paging
        const promises = [];
        const currentPage = page || 1;

        if (!dataLoaded.received || (page && page !== paginationState.received.currentPage)) {
          promises.push(fetchReceivedPackages(currentPage));
        }

        if (!dataLoaded.consolidated) {
          promises.push(fetchConsolidatedPackages());
        }

        if (!dataLoaded.shipped || (page && page !== paginationState.shipped.currentPage)) {
          promises.push(fetchShippedPackages(currentPage));
        }

        if (promises.length > 0) {
          await Promise.all(promises);
        }
      } else if (filter === 'received') {
        // Only fetch received packages if not already loaded or page changed
        if (!dataLoaded.received || (page && page !== paginationState.received.currentPage)) {
          await fetchReceivedPackages(page);
        }
      } else if (filter === 'consolidated') {
        // Only fetch consolidated packages if not already loaded
        if (!dataLoaded.consolidated) {
          await fetchConsolidatedPackages();
        }
      } else if (filter === 'shipped') {
        // Only fetch shipped packages if not already loaded or page changed
        if (!dataLoaded.shipped || (page && page !== paginationState.shipped.currentPage)) {
          await fetchShippedPackages(page);
        }
      }
    } catch (err) {
      setError(`Failed to load packages. Please try again.`);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        isLoadingRef.current = false;
      }
    }
  };

  // Initial fetch on component mount and cleanup
  useEffect(() => {
    isMountedRef.current = true;
    fetchPackages(statusFilter);

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // To track if this is the initial render
  const initialRenderRef = useRef(true);

  // Handle status filter changes
  useEffect(() => {
    // Don't run this effect on the initial mount - that's handled by the mount effect
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    fetchPackages(statusFilter);
  }, [statusFilter]);  // Filter packages is now handled by the useMemo implementation above

  const handleSelectPackage = (id: string, pkg: Package) => {
    if (!canSelectPackage(pkg)) return;

    const newSelectedPackages = selectedPackages.includes(id)
      ? selectedPackages.filter(packageId => packageId !== id)
      : [...selectedPackages, id];

    setSelectedPackages(newSelectedPackages);
    if (onSelectPackages) {
      onSelectPackages(newSelectedPackages);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectablePackages = filteredPackages.filter((pkg: Package) => canSelectPackage(pkg));
    const selectableIds = selectablePackages.map((pkg: Package) => pkg.id.toString());

    const newSelectedPackages = e.target.checked ? selectableIds : [];
    setSelectedPackages(newSelectedPackages);
    if (onSelectPackages) {
      onSelectPackages(newSelectedPackages);
    }
  };

  const handleStatusFilterChange = (value: string | null) => {
    filterDispatch({ type: 'SET_STATUS_FILTER', payload: (value || 'received') as PackageFilterType });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > paginationState.received.totalPages) return;
    fetchPackages(statusFilter, newPage);
  };

  // Use memoized filtering to avoid unnecessary re-computation
  const filteredPackages = useMemo(() => {
    const allPackages = [...receivedPackages, ...combinedPackages, ...shippedPackages];

    return allPackages.filter((pkg: Package) => {
      const matchesSearch =
        (pkg.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (pkg.item && pkg.item.some((item: any) =>
          (item.name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
        ));

      if (statusFilter === 'all') {
        return matchesSearch;
      }

      switch (statusFilter) {
        case 'received':
          return matchesSearch && pkg.type === 'regular';
        case 'consolidated':
          return matchesSearch && pkg.type === 'combined';
        case 'shipped':
          return matchesSearch && pkg.type === 'shipped';
        default:
          return matchesSearch;
      }
    });
  }, [receivedPackages, combinedPackages, shippedPackages, statusFilter, searchTerm]);

  return (
    <Stack>
      {/* Integrated Search and Filter bar */}
      <Group justify="space-between" gap="lg" wrap="nowrap">
        <TextInput
          placeholder="Search packages..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          style={{ flex: 1 }}
          visibleFrom="sm"
        />
        <SegmentedControl
          value={statusFilter}
          onChange={handleStatusFilterChange}
          data={[
            { label: 'All', value: 'all' },
            { label: 'In Warehouse', value: 'received' },
            { label: 'Consolidated', value: 'consolidated' },
            { label: 'Shipped', value: 'shipped' }
          ]}
          size="sm"
        />
      </Group>

      {loading ? (
        <Stack align="center" py="xl">
          <Loader size="md" />
          <Text size="sm" c="dimmed">Loading packages...</Text>
        </Stack>
      ) : error ? (
        <Alert icon={<IconAlertCircle size="1rem" />} title="Error" color="red">
          {error}
        </Alert>
      ) : filteredPackages.length === 0 ? (
        <Stack align="center" py="xl">
          <IconPackage size={48} stroke={1.5} opacity={0.5} />
          <Text fw={500}>No packages found</Text>
          <Text size="sm" c="dimmed" ta="center">
            There are no packages matching your search criteria
          </Text>
        </Stack>
      ) : (
        <Stack>
          {selectedPackages.length > 1 && (
            <Alert color="blue" title="Multiple packages selected">
              <Text size="sm">Multiple packages must be consolidated before shipping. Please use the Consolidate button.</Text>
            </Alert>
          )}

          <Checkbox
            onChange={handleSelectAll}
            checked={
              filteredPackages.length > 0 &&
              selectedPackages.length === filteredPackages.filter(pkg => canSelectPackage(pkg)).length
            }
            indeterminate={
              selectedPackages.length > 0 &&
              selectedPackages.length < filteredPackages.filter(pkg => canSelectPackage(pkg)).length
            }
            label="Select All"
            size="sm"
            mr="xs"
          />

          <Stack>
            {filteredPackages.map((pkg: Package) => (
              <PackageItem
                key={`${pkg.id}-${pkg.type}`}
                pkg={pkg}
                isSelected={selectedPackages.includes(pkg.id.toString())}
                onPackageSelect={handleSelectPackage}
                canSelectPackage={canSelectPackage}
                isMobile={isMobile}
              />
            ))}
          </Stack>

          {/* Action buttons at the bottom when packages are selected */}

          <PackageActionBar
            selectedPackages={selectedPackages}
            isMobile={isMobile}
            onDisposeClick={() => setDisposeModalOpen(true)}
            onReturnClick={() => setReturnModalOpen(true)}
          />


          {/* Pagination controls based on the current filter */}
          {(() => {
            // Determine which pagination state to use based on current filter
            let paginationToUse;

            switch (statusFilter) {
              case 'received':
                paginationToUse = paginationState.received;
                break;
              case 'consolidated':
                paginationToUse = paginationState.consolidated;
                break;
              case 'shipped':
                paginationToUse = paginationState.shipped;
                break;
              case 'all':
                // For 'all' filter, use received as the main pagination
                paginationToUse = paginationState.received;
                break;
              default:
                paginationToUse = paginationState.received;
            }

            // Only show pagination if there are multiple pages
            if (paginationToUse.totalPages > 1) {
              return (
                <Stack align="center" mt="md">
                  <div>
                    <button
                      onClick={() => handlePageChange(paginationToUse.currentPage - 1)}
                      disabled={paginationToUse.currentPage === 1}
                    >
                      Previous
                    </button>
                    <span style={{ margin: '0 8px' }}>
                      Page {paginationToUse.currentPage} of {paginationToUse.totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(paginationToUse.currentPage + 1)}
                      disabled={paginationToUse.currentPage === paginationToUse.totalPages}
                    >
                      Next
                    </button>
                  </div>
                </Stack>
              );
            }
            return null;
          })()}
        </Stack>
      )}

      {/* Enhanced modals with package overview and service charges */}
      <DisposeModal
        opened={disposeModalOpen}
        onClose={() => setDisposeModalOpen(false)}
        selectedPackages={filteredPackages.filter(pkg => selectedPackages.includes(pkg.id.toString()))}
        isMobile={isMobile}
      />

      <ReturnModal
        opened={returnModalOpen}
        onClose={() => setReturnModalOpen(false)}
        selectedPackages={filteredPackages.filter(pkg => selectedPackages.includes(pkg.id.toString()))}
        isMobile={isMobile}
      />
    </Stack>
  );
};

export default PackageList;
